export const reportStyles: Record<string, {
    container: string;
    font: string;
    headerWrapper: string;
    schoolTitle: string;
    subTitle: string;
    infoCard: string;
    infoLabel: string;
    infoValue: string;
    tableContainer: string;
    tableHead: string;
    tableHeadText: string;
    tableRow: string;
    tableRowAlt: string;
    gradeBadge: (grade: string) => string;
    sectionTitle: string;
    aiSection: string;
    remarksSection: string;
    footer: string;
}> = {
    default: {
        container: "bg-white p-8 shadow-2xl text-slate-800",
        font: "font-sans",
        headerWrapper: "text-center mb-6 border-b-2 border-slate-800 pb-4",
        schoolTitle: "text-2xl font-bold text-slate-900 uppercase tracking-wider",
        subTitle: "text-sm font-bold text-slate-600 uppercase tracking-widest mt-2",
        infoCard: "grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200",
        infoLabel: "text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-900",
        tableContainer: "mb-6 overflow-hidden rounded-xl border border-slate-200",
        tableHead: "bg-slate-800 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-slate-200 even:bg-slate-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-blue-700' : 'text-slate-700'}`,
        sectionTitle: "text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide",
        aiSection: "mb-6 bg-blue-50 p-4 rounded-xl border-l-4 border-blue-600 text-slate-700",
        remarksSection: "p-4 bg-white border border-slate-300 rounded-xl min-h-[60px] text-sm shadow-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-8 text-center text-sm"
    },
    modern: {
        container: "bg-slate-50 p-8 shadow-xl text-slate-700 ring-1 ring-slate-900/5",
        font: "font-sans antialiased",
        headerWrapper: "flex items-center justify-between mb-8 pb-6 border-b border-slate-200",
        schoolTitle: "text-3xl font-black text-slate-900 tracking-tight",
        subTitle: "text-xs font-semibold text-indigo-600 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full",
        infoCard: "grid grid-cols-2 md:grid-cols-4 gap-6 mb-8",
        infoLabel: "text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1",
        infoValue: "text-base font-medium text-slate-900 border-l-2 border-indigo-500 pl-3",
        tableContainer: "mb-8 bg-white shadow-sm ring-1 ring-slate-900/5 rounded-lg overflow-hidden",
        tableHead: "bg-slate-900 text-white border-b border-slate-200",
        tableHeadText: "uppercase font-semibold text-[10px] text-slate-400 tracking-wider py-4",
        tableRow: "border-b border-slate-100 hover:bg-slate-50 transition-colors",
        tableRowAlt: "",
        gradeBadge: (g) => `inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${['A+', 'A'].includes(g) ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-slate-900 mb-3 flex items-center gap-2",
        aiSection: "mb-8 bg-gradient-to-br from-white to-slate-50 p-6 rounded-2xl ring-1 ring-slate-900/5 shadow-sm",
        remarksSection: "p-4 bg-white ring-1 ring-slate-900/10 rounded-lg min-h-[60px] text-sm text-slate-600",
        footer: "grid grid-cols-3 gap-12 mt-12 pt-8 border-t border-slate-200 text-center text-xs text-slate-400"
    },
    classic: {
        container: "bg-[#fffdf9] p-12 shadow-2xl text-slate-900 max-w-[210mm] mx-auto min-h-[297mm] border-double border-4 border-slate-800",
        font: "font-serif",
        headerWrapper: "text-center mb-8 border-b-4 border-slate-900 pb-4 mx-8",
        schoolTitle: "text-4xl font-serif font-bold text-slate-900 uppercase tracking-widest mb-2",
        subTitle: "text-sm font-bold text-slate-800 uppercase tracking-[0.2em] border-y border-slate-900 py-1 inline-block",
        infoCard: "grid grid-cols-2 gap-y-4 mb-8 mx-8 border-b border-slate-900 pb-6",
        infoLabel: "text-xs font-bold text-slate-800 uppercase mr-2 inline-block min-w-[120px]",
        infoValue: "text-sm font-serif text-slate-900 inline-block border-b border-dotted border-slate-400 min-w-[150px]",
        tableContainer: "mb-8 mx-4 border-2 border-slate-900",
        tableHead: "bg-slate-100 border-b-2 border-slate-900",
        tableHeadText: "uppercase font-bold text-xs text-slate-900 border-r border-slate-900 last:border-r-0 py-2",
        tableRow: "border-b border-slate-900 last:border-b-0",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'underline decoration-double' : ''}`,
        sectionTitle: "text-center text-lg font-serif font-bold text-slate-900 mb-4 underline decoration-slate-400",
        aiSection: "mb-8 mx-8 p-4 bg-slate-50 border border-slate-900 italic font-serif text-justify",
        remarksSection: "mb-4 border-b border-slate-900 min-h-[40px] text-sm font-serif italic",
        footer: "grid grid-cols-3 gap-8 mt-16 pt-8 text-center font-serif text-sm italic"
    },
    minimal: {
        container: "bg-white p-8 shadow-sm text-gray-900 font-light",
        font: "font-sans",
        headerWrapper: "mb-12",
        schoolTitle: "text-3xl font-light text-black tracking-tighter",
        subTitle: "text-xs text-gray-400 uppercase tracking-widest mt-2",
        infoCard: "grid grid-cols-4 gap-8 mb-12",
        infoLabel: "block text-[10px] text-gray-400 uppercase mb-2",
        infoValue: "block text-xl font-light text-black",
        tableContainer: "mb-12",
        tableHead: "border-b border-black",
        tableHeadText: "text-xs font-medium text-black py-4 uppercase tracking-widest text-left",
        tableRow: "border-b border-gray-100",
        tableRowAlt: "",
        gradeBadge: (g) => `font-medium ${['A+', 'A'].includes(g) ? 'text-black' : 'text-gray-400'}`,
        sectionTitle: "text-xs font-bold text-black uppercase tracking-widest mb-6",
        aiSection: "mb-12 text-lg font-light text-gray-600 leading-relaxed border-l border-black pl-6",
        remarksSection: "border-b border-gray-200 py-2 text-sm text-gray-600",
        footer: "grid grid-cols-3 gap-12 mt-20 pt-8 border-t border-gray-100 text-xs text-gray-400"
    },
    professional: {
        container: "bg-white p-8 shadow-2xl text-slate-800",
        font: "font-sans",
        headerWrapper: "flex flex-col items-center mb-6 pb-6 border-b-2 border-blue-900",
        schoolTitle: "text-2xl font-bold text-blue-900 uppercase tracking-wide",
        subTitle: "text-xs font-bold text-slate-500 uppercase tracking-widest mt-1",
        infoCard: "grid grid-cols-4 gap-4 mb-6 bg-slate-50 p-4 border-l-4 border-blue-900",
        infoLabel: "text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-800",
        tableContainer: "mb-6 border border-slate-200",
        tableHead: "bg-blue-900 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white py-3",
        tableRow: "border-b border-slate-200 even:bg-slate-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold px-2 py-0.5 rounded ${['A+', 'A'].includes(g) ? 'bg-blue-100 text-blue-800' : 'text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-blue-900 mb-2 uppercase tracking-wide border-b border-blue-200 pb-1",
        aiSection: "mb-6 bg-slate-50 p-4 border border-slate-200 rounded text-slate-700 text-justify",
        remarksSection: "p-3 bg-white border border-slate-300 min-h-[50px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-10 pt-4 border-t border-slate-300 text-center text-xs text-slate-500"
    },
    crimson: {
        container: "bg-[#fff5f5] p-8 shadow-2xl text-red-950 border-[2px] border-red-900 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2 border-b-2 border-red-900 pb-2",
        schoolTitle: "text-xl font-bold text-red-900 uppercase tracking-widest",
        subTitle: "text-[10px] font-bold text-red-700 uppercase tracking-[0.3em] mt-2",
        infoCard: "border-y border-red-200 bg-white py-2 mb-2",
        infoLabel: "text-xs font-bold text-red-800/60 uppercase mb-1 text-center",
        infoValue: "text-sm font-bold text-red-950 text-center",
        tableContainer: "mb-2 border border-red-900",
        tableHead: "bg-red-900 text-white border-b border-red-900",
        tableHeadText: "uppercase font-bold text-[10px] text-red-50 tracking-widest",
        tableRow: "border-b border-red-200 bg-white",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-red-700' : 'text-slate-500'}`,
        sectionTitle: "text-center text-sm font-bold text-red-900 mb-1 uppercase tracking-widest",
        aiSection: "mb-2 bg-white p-2 border border-red-200 text-red-900 italic",
        remarksSection: "p-2 bg-white border border-red-900 min-h-[40px] text-sm font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-red-900/50 font-serif italic"
    },
    sunset: {
        container: "bg-white p-8 shadow-2xl text-slate-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-gradient-to-r from-orange-500 to-rose-500 text-white p-2 rounded-xl mb-2 shadow-lg",
        schoolTitle: "text-xl font-bold tracking-tight text-white",
        subTitle: "text-orange-100 text-[10px] font-bold uppercase tracking-widest mt-1",
        infoCard: "bg-orange-50 p-2 rounded-xl mb-2 border border-orange-100",
        infoLabel: "text-xs font-bold text-orange-600 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-800",
        tableContainer: "mb-2 rounded-xl overflow-hidden border border-orange-100",
        tableHead: "bg-orange-100 border-b border-orange-200",
        tableHeadText: "uppercase font-bold text-[10px] text-orange-700",
        tableRow: "border-b border-orange-50 hover:bg-orange-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-orange-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-rose-600 mb-1",
        aiSection: "mb-2 bg-gradient-to-br from-orange-50 to-rose-50 p-2 rounded-xl border border-orange-100 text-slate-700",
        remarksSection: "p-2 border border-orange-200 rounded-xl bg-white min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-orange-900/40"
    },
    glass: {
        container: "bg-slate-100 p-8 shadow-2xl text-slate-800 text-xs bg-[url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center print:bg-none",
        font: "font-sans",
        headerWrapper: "backdrop-blur-xl bg-white/70 p-2 rounded-2xl mb-2 border border-white/50 shadow-sm flex items-center justify-between",
        schoolTitle: "text-xl font-bold text-slate-800",
        subTitle: "text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1",
        infoCard: "backdrop-blur-md bg-white/60 p-2 rounded-2xl mb-2 border border-white/50",
        infoLabel: "text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-900",
        tableContainer: "mb-2 backdrop-blur-md bg-white/80 rounded-2xl overflow-hidden border border-white/50",
        tableHead: "bg-white/50 border-b border-white",
        tableHeadText: "uppercase font-bold text-[10px] text-slate-600",
        tableRow: "border-b border-slate-100/50 hover:bg-white/50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-indigo-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-slate-700 mb-1 uppercase tracking-wide backdrop-blur-sm bg-white/40 inline-block px-3 py-1 rounded-lg",
        aiSection: "mb-2 backdrop-blur-xl bg-white/70 p-2 rounded-2xl border border-white/50 text-slate-700",
        remarksSection: "p-2 backdrop-blur-md bg-white/60 border border-white/50 rounded-2xl min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-slate-600 backdrop-blur-sm bg-white/30 p-2 rounded-xl"
    },
    "classic-blue": {
        container: "bg-white p-8 shadow-2xl text-slate-900 border-2 border-blue-900 outline-0 m-0 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2 border-b-2 border-blue-900 pb-2 mx-0",
        schoolTitle: "text-xl font-bold text-blue-900 uppercase tracking-widest",
        subTitle: "text-[10px] font-bold text-blue-800 uppercase tracking-widest mt-2",
        infoCard: "mx-0 mb-2 border border-blue-200 p-2 bg-blue-50/30",
        infoLabel: "text-xs font-bold text-blue-800/70 uppercase mb-1 text-center",
        infoValue: "text-sm font-bold text-blue-950 text-center",
        tableContainer: "mx-0 mb-2 border-2 border-blue-900",
        tableHead: "bg-blue-900 text-white border-b-2 border-blue-900",
        tableHeadText: "uppercase font-bold text-[10px] text-white tracking-widest",
        tableRow: "border-b border-blue-200",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-blue-800 underline' : 'text-slate-500'}`,
        sectionTitle: "text-center text-sm font-bold text-blue-900 mb-1 uppercase tracking-widest border-b border-blue-200 w-1/3 mx-auto pb-2",
        aiSection: "mx-0 mb-2 p-2 border-l-4 border-blue-900 bg-slate-50 text-slate-800 italic",
        remarksSection: "mx-0 p-2 border border-blue-900 min-h-[40px] text-sm font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-blue-900/60 font-serif italic mx-0"
    },
    nature: {
        container: "bg-stone-50 p-8 shadow-2xl text-stone-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-emerald-800 text-emerald-50 p-2 rounded-tl-[2rem] rounded-br-[2rem] mb-2 flex justify-between items-center",
        schoolTitle: "text-xl font-bold tracking-tight text-white",
        subTitle: "text-emerald-200 text-[10px] font-medium uppercase tracking-widest",
        infoCard: "mb-2 bg-white/50 p-2 rounded-2xl border border-emerald-100/50 shadow-sm",
        infoLabel: "text-xs font-bold text-emerald-700 uppercase mb-1",
        infoValue: "text-sm text-stone-700 border-l-2 border-emerald-300 pl-3",
        tableContainer: "border-2 border-emerald-100 rounded-xl overflow-hidden mb-2",
        tableHead: "bg-emerald-50 border-b border-emerald-100",
        tableHeadText: "uppercase font-bold text-[10px] text-emerald-800",
        tableRow: "border-b border-emerald-50/50 hover:bg-emerald-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-emerald-600' : 'text-stone-500'}`,
        sectionTitle: "text-sm font-bold text-emerald-800 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-emerald-50/50 p-2 rounded-2xl border border-emerald-100 text-stone-700",
        remarksSection: "p-3 border border-stone-200 rounded-xl bg-white min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-stone-400"
    },
    bold: {
        container: "bg-white p-8 shadow-2xl text-black border-x-4 border-red-600 text-xs",
        font: "font-sans",
        headerWrapper: "border-b-4 border-black pb-2 mb-2",
        schoolTitle: "text-2xl font-black text-black uppercase tracking-tighter transform -skew-x-6",
        subTitle: "text-xs font-bold text-red-600 uppercase tracking-widest transform -skew-x-6",
        infoCard: "bg-black text-white p-2 mb-2 transform -skew-x-2",
        infoLabel: "text-xs font-bold text-red-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-white",
        tableContainer: "mb-2 border-2 border-black",
        tableHead: "bg-red-600 text-white border-b-2 border-black",
        tableHeadText: "uppercase font-bold text-[10px] text-white tracking-widest",
        tableRow: "border-b-2 border-slate-200",
        tableRowAlt: "",
        gradeBadge: (g) => `font-black text-red-600 uppercase ${['A+', 'A'].includes(g) ? 'underline decoration-4 decoration-black' : ''}`,
        sectionTitle: "text-sm font-black text-black uppercase border-b-4 border-red-600 inline-block mb-1",
        aiSection: "mb-2 border-4 border-black p-2 font-bold text-black",
        remarksSection: "border-4 border-black p-2 min-h-[40px] font-bold text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center font-black uppercase text-xs"
    },
    creative: {
        container: "bg-white p-8 shadow-2xl text-slate-800 border-t-4 border-purple-500 text-xs",
        font: "font-sans",
        headerWrapper: "mb-2 flex justify-between items-center bg-purple-50 p-2 rounded-3xl",
        schoolTitle: "text-xl font-bold text-purple-600",
        subTitle: "text-[10px] text-purple-400 font-medium mt-1",
        infoCard: "mb-2",
        infoLabel: "text-xs font-bold text-purple-400 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700 bg-purple-50 p-2 rounded-xl text-center",
        tableContainer: "overflow-hidden rounded-2xl border-2 border-purple-100 mb-2",
        tableHead: "bg-purple-500 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-purple-50 hover:bg-purple-50/50",
        tableRowAlt: "",
        gradeBadge: (g) => `inline-block px-3 py-1 rounded-full text-[10px] font-bold ${['A+', 'A'].includes(g) ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-purple-600 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-gradient-to-r from-purple-500 to-pink-500 p-1 rounded-2xl text-white",
        remarksSection: "p-2 border-2 border-dashed border-purple-200 rounded-2xl min-h-[40px] text-sm text-slate-700 bg-purple-50/30",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-purple-900"
    },
    tech: {
        container: "bg-slate-900 p-8 shadow-2xl text-green-400 font-mono text-xs",
        font: "font-mono",
        headerWrapper: "border-b border-green-800 pb-2 mb-2 flex justify-between items-end",
        schoolTitle: "text-lg font-bold text-green-500 uppercase tracking-widest",
        subTitle: "text-xs text-green-700 uppercase tracking-widest mt-1",
        infoCard: "bg-slate-800 border border-green-900/50 p-2 mb-2",
        infoLabel: "text-xs text-green-600 uppercase mb-1",
        infoValue: "text-sm text-green-300",
        tableContainer: "border border-green-800 mb-2",
        tableHead: "bg-green-900/20 border-b border-green-800",
        tableHeadText: "uppercase font-bold text-[10px] text-green-500",
        tableRow: "border-b border-green-900/50 hover:bg-green-900/10",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-green-400 text-shadow' : 'text-green-700'}`,
        sectionTitle: "text-xs font-bold text-green-600 mb-1 uppercase tracking-widest border-b border-green-900 w-fit pr-4",
        aiSection: "mb-2 bg-slate-800 p-2 border border-green-700 text-green-300 text-sm",
        remarksSection: "p-3 border border-green-800 bg-slate-950 min-h-[40px] text-sm text-green-500",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-green-800 text-xs"
    },
    elegant: {
        container: "bg-[#F9F7F2] p-8 shadow-xl text-slate-800 border-t-4 border-amber-700 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2",
        schoolTitle: "text-xl font-serif italic text-amber-900 mb-1",
        subTitle: "text-xs font-bold text-amber-700/60 uppercase tracking-[0.3em]",
        infoCard: "border-y border-amber-200 py-2 mb-2 bg-white/50",
        infoLabel: "text-xs font-bold text-amber-800/60 uppercase mb-1 tracking-wider text-center",
        infoValue: "text-sm text-amber-950 text-center font-serif",
        tableContainer: "mb-2",
        tableHead: "border-b-2 border-amber-800/20",
        tableHeadText: "uppercase font-bold text-[10px] text-amber-900 tracking-widest py-1",
        tableRow: "border-b border-amber-100 hover:bg-amber-50/50 transition-colors",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold font-serif ${['A+', 'A'].includes(g) ? 'text-amber-700' : 'text-slate-500'}`,
        sectionTitle: "text-sm italic font-serif text-amber-900 mb-1 text-center",
        aiSection: "mb-2 p-2 bg-white border border-amber-100 shadow-sm italic text-slate-600",
        remarksSection: "p-2 bg-white border border-amber-200 min-h-[40px] text-sm text-slate-700 font-serif shadow-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-amber-900/50 font-serif italic"
    },
    formal: {
        container: "bg-[#fffdf5] p-8 shadow-2xl text-slate-900 border-4 border-double border-slate-800 text-xs",
        font: "font-serif",
        headerWrapper: "text-center border-b border-slate-400 pb-1 mb-1",
        schoolTitle: "text-xl font-serif font-bold text-slate-900 uppercase tracking-widest",
        subTitle: "text-[10px] text-slate-600 italic mt-2 font-serif",
        infoCard: "bg-transparent mb-2 border-y border-slate-300 py-2",
        infoLabel: "block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 text-center",
        infoValue: "text-sm text-center text-slate-800",
        tableContainer: "mb-2 border border-slate-800",
        tableHead: "bg-slate-200 border-b border-slate-800",
        tableHeadText: "uppercase font-bold text-[10px] text-slate-800",
        tableRow: "border-b border-slate-300",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'underline decoration-double' : ''}`,
        sectionTitle: "text-center text-sm font-bold text-slate-800 mb-1 font-serif italic border-b border-slate-200 pb-2 mx-auto w-1/2",
        aiSection: "mb-2 p-2 border border-slate-300 bg-white italic",
        remarksSection: "p-2 border border-slate-400 min-h-[40px] text-sm text-slate-800 font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center"
    },
    corporate: {
        container: "bg-slate-50 p-8 shadow-2xl text-slate-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-slate-800 p-6 -mx-8 -mt-8 mb-2 text-white flex justify-between items-center shadow-md",
        schoolTitle: "text-lg font-bold text-white uppercase tracking-wide",
        subTitle: "text-xs text-slate-300 uppercase tracking-widest mt-1",
        infoCard: "bg-white shadow-sm rounded-sm p-2 mb-2 border-l-4 border-blue-600",
        infoLabel: "flex items-center gap-2 text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700",
        tableContainer: "bg-white shadow-sm mb-2",
        tableHead: "bg-slate-800 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-slate-200 even:bg-slate-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-blue-700' : 'text-slate-700'}`,
        sectionTitle: "text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide",
        aiSection: "mb-2 bg-blue-50 p-2 border-l-4 border-blue-600 text-slate-700",
        remarksSection: "p-2 bg-white border border-slate-300 min-h-[40px] text-sm shadow-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-sm"
    },
    royal: {
        container: "bg-white p-8 shadow-2xl text-slate-900 border-4 border-double border-yellow-600 text-xs",
        font: "font-serif",
        headerWrapper: "bg-blue-950 p-2 mb-2 flex items-center justify-between text-yellow-50 border-b-4 border-yellow-600",
        schoolTitle: "text-xl font-serif font-bold text-yellow-500 uppercase tracking-widest",
        subTitle: "text-[10px] text-yellow-100 uppercase tracking-[0.2em] mt-1",
        infoCard: "bg-blue-50 border border-blue-100 p-2 mb-2",
        infoLabel: "text-[10px] font-bold text-blue-900 uppercase mb-1",
        infoValue: "text-sm font-bold text-blue-950",
        tableContainer: "mb-2 border border-blue-900",
        tableHead: "bg-blue-900 border-b-4 border-yellow-600",
        tableHeadText: "uppercase font-bold text-[10px] text-yellow-500 tracking-wider",
        tableRow: "border-b border-blue-100 hover:bg-blue-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-blue-900' : 'text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-blue-900 mb-1 uppercase tracking-wide border-b-2 border-yellow-500 w-fit",
        aiSection: "mb-2 bg-white p-2 border border-yellow-600/30 text-slate-800 shadow-sm",
        remarksSection: "p-3 border border-blue-200 bg-blue-50/30 min-h-[40px] text-sm text-blue-900 font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-blue-900 font-serif"
    },
    slate: {
        container: "bg-slate-50 p-8 shadow-xl text-slate-900 text-xs",
        font: "font-sans",
        headerWrapper: "border-b-2 border-slate-900 pb-2 mb-2 flex justify-between items-start",
        schoolTitle: "text-2xl font-black text-slate-900 tracking-tighter",
        subTitle: "text-[10px] font-bold text-slate-500 uppercase tracking-tight mt-1",
        infoCard: "bg-white p-2 border border-slate-200 mb-2 shadow-sm",
        infoLabel: "text-xs font-bold text-slate-400 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-900",
        tableContainer: "mb-2",
        tableHead: "bg-slate-900",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-slate-200 bg-white hover:bg-slate-100",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold px-2 py-0.5 bg-slate-100 rounded ${['A+', 'A'].includes(g) ? 'text-slate-900' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-black text-slate-900 mb-1 uppercase tracking-wider",
        aiSection: "mb-2 bg-slate-900 text-slate-300 p-2 font-light text-xs leading-relaxed",
        remarksSection: "p-2 bg-white border-2 border-slate-900 min-h-[40px] text-sm font-bold",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-slate-900 font-bold uppercase text-[10px]"
    },
    mint: {
        container: "bg-[#f0fdfa] p-8 shadow-xl text-teal-900 text-xs",
        font: "font-sans",
        headerWrapper: "mb-2 flex items-center gap-4 bg-teal-100/50 p-2 rounded-xl",
        schoolTitle: "text-xl font-bold text-teal-800",
        subTitle: "text-xs text-teal-600 uppercase tracking-widest mt-1",
        infoCard: "mb-2 bg-white/60 p-2 rounded-xl border border-teal-100",
        infoLabel: "text-xs font-bold text-teal-500 uppercase mb-1",
        infoValue: "text-sm font-semibold text-teal-900",
        tableContainer: "mb-2 border border-teal-100 rounded-lg overflow-hidden bg-white/60",
        tableHead: "bg-teal-50 border-b border-teal-100",
        tableHeadText: "uppercase font-bold text-[10px] text-teal-700",
        tableRow: "border-b border-teal-50 hover:bg-teal-50/50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-teal-600' : 'text-teal-900/50'}`,
        sectionTitle: "text-sm font-bold text-teal-800 mb-1 uppercase tracking-wider pl-2 border-l-4 border-teal-400",
        aiSection: "mb-2 bg-teal-50 p-2 rounded-xl border border-teal-200 text-teal-800",
        remarksSection: "p-2 bg-white border border-teal-200 rounded-xl min-h-[40px] text-sm text-teal-900",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-teal-800/40"
    },
    oceanic: {
        container: "bg-cyan-50 p-8 shadow-2xl text-cyan-950 text-xs",
        font: "font-sans",
        headerWrapper: "bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-2 rounded-lg mb-2 shadow-lg",
        schoolTitle: "text-xl font-bold uppercase tracking-wide",
        subTitle: "text-xs text-cyan-100 uppercase tracking-widest mt-1 opacity-80",
        infoCard: "bg-white p-2 rounded-lg shadow-sm border-l-4 border-cyan-500 mb-2",
        infoLabel: "text-xs font-bold text-cyan-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700",
        tableContainer: "mb-2 bg-white rounded-lg shadow-sm overflow-hidden",
        tableHead: "bg-cyan-700 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white tracking-wider",
        tableRow: "border-b border-cyan-50 even:bg-cyan-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+', 'A'].includes(g) ? 'text-cyan-600' : 'text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-cyan-800 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-white p-2 border-l-4 border-blue-500 shadow-sm text-slate-600",
        remarksSection: "p-3 bg-white border border-cyan-200 rounded min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-cyan-800 text-xs"
    },
    blossom: {
        container: "bg-white p-8 shadow-xl text-slate-800 rounded-none border-0 text-xs",
        font: "font-sans",
        headerWrapper: "bg-pink-50 p-2 rounded-3xl mb-2 flex flex-col items-center justify-center text-center",
        schoolTitle: "text-xl font-bold text-pink-500 tracking-tight",
        subTitle: "text-xs text-pink-400 font-medium uppercase tracking-widest mt-1 bg-white px-3 py-1 rounded-full",
        infoCard: "mb-2",
        infoLabel: "text-[10px] font-bold text-pink-300 uppercase mb-1 text-center",
        infoValue: "text-sm text-slate-700 bg-pink-50/50 p-2 rounded-xl text-center border border-pink-100",
        tableContainer: "border border-pink-100 rounded-2xl overflow-hidden mb-2",
        tableHead: "bg-pink-400 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-pink-50 hover:bg-pink-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold inline-block px-2 py-0.5 rounded-lg ${['A+', 'A'].includes(g) ? 'bg-pink-100 text-pink-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-pink-500 mb-1 uppercase tracking-wide text-center",
        aiSection: "mb-2 bg-gradient-to-br from-pink-50 to-white p-2 rounded-3xl border border-pink-100 text-slate-600 text-center",
        remarksSection: "p-2 border border-pink-200 rounded-3xl bg-pink-50/20 min-h-[40px] text-sm text-slate-700 text-center",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-pink-300"
    }
};
